import AuthoritySelect from 'Components/AuthoritySelect'
import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import NumberFormField from 'Components/NumberFormField'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import { WithLoader } from 'Components/WithLoader'
import ZoneSelect from 'Components/ZoneSelect'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import BulkTicketSaleBottomBar from 'Services/SalesManagement/Features/TicketSale/Components/BulkTicketSaleBottomBar'
import BulkTicketSaleProductSelect from 'Services/SalesManagement/Features/TicketSale/Components/BulkTicketSaleProductSelect'
import TicketSaleTargetSelect from 'Services/SalesManagement/Features/TicketSale/Components/TicketSaleTargetSelect'
import useAllAgeGroupProducts from 'Services/SalesManagement/Features/TicketSale/Hooks/useAllAgeGroupProducts'
import { useBulkTicketSaleFormFunctions } from 'Services/SalesManagement/Features/TicketSale/Hooks/useBulkTicketSaleFormFunctions'
import { useSalesChannelData } from 'Services/SalesManagement/Features/TicketSale/Hooks/useSalesChannelData'
import { SELECTED_TICKET_SALE_PARAMETER_KEY } from 'Services/SalesManagement/Features/TicketSale/Hooks/useTicketSalesParams'
import {
  getProductId,
  OPTIONAL_DATE_PRODUCT_IDS
} from 'Services/SalesManagement/Features/TicketSale/ticketSaleProductInfo'
import type { BulkTicketSale } from 'Services/SalesManagement/Features/TicketSale/types'
import type { FieldValidationErrors } from 'Services/SalesManagement/types'
import useTypedSelector from 'Store'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { formatToUTCISO } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Box, Grid, type SelectChangeEvent, TextField } from '@mui/material'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  target: [],
  external_product_id: [],
  external_age_group_id: [],
  external_zone_from_id: [],
  external_zone_from_name: [],
  external_zone_to_id: [],
  external_zone_to_name: [],
  valid_from: [],
  valid_to: [],
  event_identification: [],
  authority: [],
  total_requested_quantity: []
}

type BulkTicketSaleFormState = Partial<BulkTicketSale> & {
  authority?: string
  total_requested_quantity?: number
}

const BULK_TICKET_SALE_FORM_ID = 'bulk-ticket-sale-form'

interface BulkTicketSaleFormProps {
  onCancel: () => void
}

export default function BulkTicketSaleForm({ onCancel }: Readonly<BulkTicketSaleFormProps>) {
  const navigate = useNavigate()
  const { user } = useTypedSelector(state => state.user)
  const { authorities } = useAuthorityContext()

  const [ticketSaleDetails, setTicketSaleDetails] = useState<BulkTicketSaleFormState>({
    authority: isSuperuser(user) ? undefined : user?.pta
  })

  const { singleSalesChannel, salesChannelInfo, isSalesChannelInfoFetching } = useSalesChannelData(
    ticketSaleDetails.authority
  )

  const { allProducts, isLoading: isProductsLoading } = useAllAgeGroupProducts(
    salesChannelInfo,
    singleSalesChannel?.external_id
  )

  const {
    ticketSaleTargets,
    isTicketSaleTargetsFetching,
    createBulkTicketSale,
    isCreateBulkTicketSaleLoading,
    createBulkTicketSaleBatch,
    isCreateBulkTicketSaleBatchLoading
  } = useBulkTicketSaleFormFunctions()

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)

  const handleChange = (
    value: string | string[] | number | undefined,
    field: keyof BulkTicketSaleFormState
  ) => {
    if (value !== undefined) {
      setTicketSaleDetails(prevState => ({ ...prevState, [field]: value }))
      setFieldErrors(prevState => ({ ...prevState, [field]: [] }))
    }
  }

  const handleZoneChange = (e: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
    const zoneId = e.target.value as number
    const zoneName = salesChannelInfo?.zones.find(zone => zone.id === zoneId)?.name ?? ''
    const fieldName = e.target.name
    setTicketSaleDetails(prevState => {
      const next = {
        ...prevState,
        [`${fieldName}_id`]: zoneId,
        [`${fieldName}_name`]: zoneName
      }
      // If zone_from changed and zone_to id is now smaller, clear zone_to
      if (
        fieldName === 'external_zone_from' &&
        prevState.external_zone_to_id !== undefined &&
        zoneId > prevState.external_zone_to_id
      ) {
        next.external_zone_to_id = undefined
        next.external_zone_to_name = undefined
      }
      return next
    })
    setFieldErrors(prevState => ({
      ...prevState,
      [`${fieldName}_id`]: []
    }))
  }

  const zoneToOptions = (salesChannelInfo?.zones ?? []).filter(
    zone =>
      ticketSaleDetails.external_zone_from_id === undefined ||
      zone.id >= ticketSaleDetails.external_zone_from_id
  )

  const handleProductChange = ({
    productId,
    ageGroupId
  }: {
    productId: string
    ageGroupId: number
  }) => {
    setTicketSaleDetails(prevState => ({
      ...prevState,
      external_product_id: productId,
      external_age_group_id: ageGroupId
    }))
    setFieldErrors(prevState => ({
      ...prevState,
      external_product_id: [],
      external_age_group_id: []
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setFieldErrors(fieldErrorsDefaultState)

    const { total_requested_quantity, ...bulkTicketSaleData } = ticketSaleDetails

    if (!total_requested_quantity) {
      setFieldErrors(prevState => ({
        ...prevState,
        total_requested_quantity: [t`Ticket quantity is required`]
      }))
      return
    }

    const sale = await createBulkTicketSale(bulkTicketSaleData)
      .unwrap()
      .catch(err => {
        if (err && 'data' in err) {
          const validationErrors = err.data as FieldValidationErrors
          if (validationErrors) {
            setFieldErrors({ ...fieldErrorsDefaultState, ...validationErrors })
          } else {
            SnackbarUtils.error(t`There was an error creating the bulk sale.`)
          }
        }
      })

    if (sale) {
      const listUrl = appendQueryParamsToUrl(
        `${SALES_MANAGEMENT_ROOT_URL}/ticket-sales/bulk-ticket-sales`,
        { [SELECTED_TICKET_SALE_PARAMETER_KEY]: sale.id }
      )
      createBulkTicketSaleBatch({
        bulkSaleId: sale.id,
        requested_quantity: total_requested_quantity
      })
        .unwrap()
        .then(() => {
          navigate(listUrl)
        })
        .catch(err => {
          if (err && 'data' in err && err.data?.requested_quantity) {
            setFieldErrors(prevState => ({
              ...prevState,
              total_requested_quantity: err.data.requested_quantity
            }))
          } else {
            SnackbarUtils.error(t`Ticket sale created but batch creation failed.`)
          }
          navigate(listUrl)
        })
    }
  }

  const handleDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }
    handleChange(formattedValue, name as keyof BulkTicketSale)
  }

  const isLoading = isSalesChannelInfoFetching || isTicketSaleTargetsFetching

  const isSubmitLoading = isCreateBulkTicketSaleLoading || isCreateBulkTicketSaleBatchLoading

  const isOptionalDateProduct =
    ticketSaleDetails.external_product_id !== undefined &&
    OPTIONAL_DATE_PRODUCT_IDS.includes(getProductId(ticketSaleDetails.external_product_id))

  const validFromRequired = !isOptionalDateProduct || !!ticketSaleDetails.valid_to
  const validToRequired = !isOptionalDateProduct || !!ticketSaleDetails.valid_from

  const formRef = useRef<HTMLFormElement>(null)

  return (
    <WithLoader loading={isLoading}>
      <Grid container spacing={2} mb={4} pb={10}>
        <Grid size={12}>
          <StyledForm
            id={BULK_TICKET_SALE_FORM_ID}
            onSubmit={handleSubmit}
            isDisabled={isSubmitLoading}
            formRef={formRef}
          >
            <StyledLabel>{t`Validity area and ticket quantity`}</StyledLabel>
            <Box maxWidth={440}>
              {isSuperuser(user) && (
                <AuthoritySelect
                  sx={{ width: '100%' }}
                  value={ticketSaleDetails.authority ?? ''}
                  onChange={value => handleChange(value, 'authority')}
                  authorities={authorities}
                  error={fieldErrors.authority[0]}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <ZoneSelect
                  label={t`Zone from`}
                  name="external_zone_from"
                  value={ticketSaleDetails.external_zone_from_id?.toString() ?? ''}
                  onChange={handleZoneChange}
                  error={fieldErrors.external_zone_from_id[0]}
                  zones={salesChannelInfo?.zones ?? []}
                  required
                />
                <Box>{` - `}</Box>
                <ZoneSelect
                  label={t`Zone to`}
                  name="external_zone_to"
                  value={ticketSaleDetails.external_zone_to_id?.toString() ?? ''}
                  onChange={handleZoneChange}
                  error={fieldErrors.external_zone_to_id[0]}
                  zones={zoneToOptions}
                  required
                />
              </Box>
              <NumberFormField
                sx={{ width: '100%' }}
                unit={t`pcs`}
                required
                name="total_requested_quantity"
                label={t`Enter ticket quantity`}
                value={ticketSaleDetails?.total_requested_quantity ?? ''}
                inputProps={{ min: 1, max: 25000 }}
                errorText={
                  fieldErrors.total_requested_quantity.length > 0
                    ? fieldErrors.total_requested_quantity[0]
                    : ''
                }
                helperText={t`You can create up to 25,000 tickets at a time`}
                onChange={e =>
                  handleChange(
                    (e.target as HTMLInputElement).valueAsNumber,
                    'total_requested_quantity'
                  )
                }
                defaultOptions={[250, 500, 1000]}
              />
            </Box>
            <StyledLabel paddingTop={1}>{t`Ticket type`}</StyledLabel>
            <BulkTicketSaleProductSelect
              salesChannelId={singleSalesChannel?.external_id}
              allProducts={allProducts}
              isLoading={isProductsLoading}
              value={ticketSaleDetails.external_product_id ?? ''}
              onChange={handleProductChange}
              error={fieldErrors.external_product_id[0]}
            />
            <StyledLabel paddingTop={1}>{t`Validity`}</StyledLabel>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <CustomDateTimePicker
                name="valid_from"
                value={ticketSaleDetails.valid_from ? new Date(ticketSaleDetails.valid_from) : null}
                dateToCompare={
                  ticketSaleDetails.valid_to ? new Date(ticketSaleDetails.valid_to) : null
                }
                dateToCompareFieldName="valid_to"
                isStartDate={true}
                onChange={handleDateTimePickerChange}
                label={t`Valid from`}
                isRequired={validFromRequired}
                isError={fieldErrors.valid_from.length > 0}
                helperText={fieldErrors.valid_from.length > 0 ? fieldErrors.valid_from[0] : ''}
              />
              <Box pt={4}>{` - `}</Box>
              <CustomDateTimePicker
                name="valid_to"
                value={ticketSaleDetails.valid_to ? new Date(ticketSaleDetails.valid_to) : null}
                dateToCompare={
                  ticketSaleDetails.valid_from ? new Date(ticketSaleDetails.valid_from) : null
                }
                dateToCompareFieldName="valid_from"
                isStartDate={false}
                onChange={handleDateTimePickerChange}
                label={t`Valid to`}
                isRequired={validToRequired}
                minDateTime={new Date()}
                isError={fieldErrors.valid_to.length > 0}
                helperText={fieldErrors.valid_to.length > 0 ? fieldErrors.valid_to[0] : ''}
              />
            </Box>
            <StyledLabel paddingTop={1}>{t`Additional information`}</StyledLabel>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <TicketSaleTargetSelect
                targets={ticketSaleTargets ?? []}
                sx={{ minWidth: 250 }}
                value={ticketSaleDetails.target ?? ''}
                onChange={value => handleChange(value, 'target')}
                error={fieldErrors.target[0]}
              />
              <TextField
                sx={{ minWidth: 250 }}
                name="event_identification"
                label={t`Event identifier`}
                value={ticketSaleDetails?.event_identification ?? ''}
                required
                error={fieldErrors.event_identification.length > 0}
                helperText={
                  fieldErrors.event_identification.length > 0
                    ? fieldErrors.event_identification[0]
                    : ''
                }
                onChange={e => handleChange(e.target.value, 'event_identification')}
              />
            </Box>
          </StyledForm>
        </Grid>
      </Grid>
      <BulkTicketSaleBottomBar
        ticketAmount={ticketSaleDetails.total_requested_quantity}
        zoneFromName={ticketSaleDetails.external_zone_from_name}
        zoneToName={ticketSaleDetails.external_zone_to_name}
        onCancel={onCancel}
        isLoading={isSubmitLoading}
        actionButtonLabel={t`Create tickets`}
        onActionButtonClick={() => formRef.current?.requestSubmit()}
      />
    </WithLoader>
  )
}
